import { User } from "firebase/auth";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import {
  getSyndicatePicks,
  getSyndicateMemberPicks,
  saveSyndicatePicks,
  SyndicatePicksResponse,
  SyndicatePick,
  SyndicatePot,
} from "../api/syndicate";
import { fetchClubs, Player } from "../api/players";
import { fetchLeague, LeagueDetail, LeagueStatus } from "../api/leagues";
import PageLoader from "../components/PageLoader";
import StatBox from "../components/league/StatBox";
import TrajectoryPanel from "../components/league/TrajectoryPanel";
import SyndicatePlayerPicker from "../components/league/SyndicatePlayerPicker";
import WdlChips from "../components/league/WdlChips";
import StatusDot from "../components/StatusDot";
import ClubBadge from "../components/ClubBadge";
import HowToPlayModal from "../components/HowToPlayModal";
import ModeIconSmall from "../components/league/ModeIconSmall";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import { getClubBadge } from "../data/clubBadges";
import { card, widePageContainer, primaryBtn, secondaryBtn } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props { user: User; }

export type LocalPick = {
  clubId: string | null;
  playerId: string | null;
  playerName: string | null;
  playerClub: string | null;
  playerPosition: string | null;
};

export const EMPTY_PICK: LocalPick = { clubId: null, playerId: null, playerName: null, playerClub: null, playerPosition: null };

const iconBtn: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--color-accent)",
  borderRadius: 8,
  color: "var(--color-accent)",
  cursor: "pointer",
  padding: "7px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  fontSize: 12,
  // 44px minimum touch target (CLAUDE.md) — this header icon button is tapped often.
  minHeight: 44,
  minWidth: 44,
  fontFamily: "var(--font-body)",
};

const STAT_LABEL_KEY: Record<string, string> = {
  GOALS: "syndicate.stat.GOALS",
  ASSISTS: "syndicate.stat.ASSISTS",
  YELLOW_CARDS: "syndicate.stat.YELLOW_CARDS",
  RED_CARDS: "syndicate.stat.RED_CARDS",
};

export default function SyndicatePage({ user }: Props) {
  const { id: leagueId, memberId } = useParams<{ id: string; memberId?: string }>();
  const isMemberView = !!memberId;
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [memberName, setMemberName] = useState("");
  const [leagueStatus, setLeagueStatus] = useState<LeagueStatus>("PREPARING");
  const [competitionName, setCompetitionName] = useState("");
  const [competitionSeason, setCompetitionSeason] = useState("");
  const [league, setLeague] = useState<LeagueDetail | null>(null);
  const [data, setData] = useState<SyndicatePicksResponse | null>(null);
  const [picks, setPicks] = useState<Record<string, LocalPick>>({});
  const [tiebreaker, setTiebreaker] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [clubs, setClubs] = useState<string[]>([]);
  const [openPotId, setOpenPotId] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  useBreadcrumbs(
    leagueName
      ? [
          { label: t("common.home"), to: "/overview" },
          { label: leagueName, to: `/leagues/${leagueId}` },
          { label: isMemberView && memberName ? `${memberName}${t("syndicate.titleSuffix")}` : t("syndicate.myBreadcrumb") },
        ]
      : [{ label: t("common.home"), to: "/overview" }],
  );

  const picksToLocal = useCallback((rows: SyndicatePick[]): Record<string, LocalPick> => {
    const out: Record<string, LocalPick> = {};
    for (const p of rows) {
      out[p.potId] = {
        clubId: p.clubId,
        playerId: p.playerId,
        playerName: p.player?.name ?? null,
        playerClub: p.player?.club ?? null,
        playerPosition: p.player?.position ?? null,
      };
    }
    return out;
  }, []);

  const load = useCallback(async () => {
    if (!leagueId) return;
    try {
      if (isMemberView && memberId) {
        const [leagueData, syndicate, memberData] = await Promise.all([
          fetchLeague(user, leagueId),
          getSyndicatePicks(user, leagueId),
          getSyndicateMemberPicks(user, leagueId, memberId),
        ]);
        setLeagueName(leagueData.name);
        setLeagueStatus(leagueData.status);
        setCompetitionName(leagueData.competition.name);
        setCompetitionSeason(leagueData.competition.season);
        setLeague(leagueData);
        const found = leagueData.members.find((m) => m.id === memberId);
        setMemberName(found?.displayName ?? t("syndicate.member"));
        setData({ ...syndicate, picks: memberData.picks, tiebreaker: memberData.tiebreaker, totalScore: memberData.totalScore, myDelta: memberData.myDelta, isLocked: memberData.isLocked, rankHistory: memberData.rankHistory });
        setPicks(picksToLocal(memberData.picks));
        if (memberData.tiebreaker !== null) setTiebreaker(String(memberData.tiebreaker));
      } else {
        const [leagueData, syndicate] = await Promise.all([
          fetchLeague(user, leagueId),
          getSyndicatePicks(user, leagueId),
        ]);
        setLeagueName(leagueData.name);
        setLeagueStatus(leagueData.status);
        setCompetitionName(leagueData.competition.name);
        setCompetitionSeason(leagueData.competition.season);
        setLeague(leagueData);
        setData(syndicate);
        setPicks(picksToLocal(syndicate.picks));
        if (syndicate.tiebreaker !== null) setTiebreaker(String(syndicate.tiebreaker));
      }
    } catch {
      setError(t("syndicate.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [leagueId, memberId, isMemberView, user, t, picksToLocal]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!league) return;
    fetchClubs(user, league.competition.id).then(setClubs).catch(() => {});
  }, [league, user]);

  // Recomputes which pot the accordion opens on every fresh load — the pot
  // holding an invalid (post-hoc excluded) pick wins over fill-order, so it
  // can never be silently buried in a collapsed row. Runs from server truth
  // (`data.picks`), not local edit state, so it's stable across saves.
  useEffect(() => {
    if (!data || isMemberView || data.isLocked) return;
    const filledPotIds = new Set(data.picks.filter((p) => p.clubId || p.playerId).map((p) => p.potId));
    const invalidPotIds = new Set(data.picks.filter((p) => p.invalid).map((p) => p.potId));
    const sorted = [...data.pots].sort((a, b) => a.order - b.order);
    const firstInvalid = sorted.find((p) => invalidPotIds.has(p.id));
    const firstUnfilled = sorted.find((p) => !filledPotIds.has(p.id));
    setOpenPotId(firstInvalid?.id ?? firstUnfilled?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const toggleAccordion = (potId: string) => {
    setOpenPotId((prev) => (prev === potId ? null : potId));
  };

  const selectClub = (potId: string, clubId: string) => {
    setPicks((prev) => {
      const current = prev[potId];
      const cleared = current?.clubId === clubId;
      return { ...prev, [potId]: cleared ? { ...EMPTY_PICK } : { ...EMPTY_PICK, clubId } };
    });
  };

  const selectPlayer = (potId: string, player: Player) => {
    setPicks((prev) => ({
      ...prev,
      [potId]: { clubId: null, playerId: player.id, playerName: player.name, playerClub: player.club, playerPosition: player.position },
    }));
  };

  const clearPick = (potId: string) => {
    setPicks((prev) => ({ ...prev, [potId]: { ...EMPTY_PICK } }));
  };

  const filledCount = data ? data.pots.filter((p) => picks[p.id]?.clubId || picks[p.id]?.playerId).length : 0;

  const handleSave = async () => {
    if (!leagueId || !data) return;
    setSaving(true);
    setSaveError("");
    try {
      // Send every pot's current local state, not just filled ones — a pot the
      // member cleared locally must reach the server as an explicit
      // {clubId:null, playerId:null} clear, or the old pick silently survives
      // (the PUT only ever touches pots present in the payload).
      const payload = data.pots.map((p) => ({
        potId: p.id,
        clubId: picks[p.id]?.clubId ?? null,
        playerId: picks[p.id]?.playerId ?? null,
      }));
      await saveSyndicatePicks(user, leagueId, payload, tiebreaker !== "" ? parseInt(tiebreaker, 10) : undefined);
      await load();
    } catch {
      setSaveError(t("syndicate.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;
  if (error || !data) return <div style={{ padding: 24, color: "var(--color-danger)" }}>{error}</div>;

  const pointsByPot = new Map(data.picks.map((p) => [p.potId, p.points]));
  const invalidByPot = new Map(data.picks.map((p) => [p.potId, p.invalid ?? false]));
  const formByPot = new Map(data.picks.map((p) => [p.potId, p.form]));
  const statCountByPot = new Map(data.picks.map((p) => [p.potId, p.statCount]));
  const myRank = (() => {
    if (!league) return null;
    const viewedMemberId = isMemberView ? memberId : league.myMemberId;
    if (!viewedMemberId) return null;
    const approved = league.members.filter((m) => m.status === "APPROVED" && m.isTeamValid);
    const sorted = [...approved].sort((a, b) => b.score - a.score);
    let rank = 1;
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i].score < sorted[i - 1].score) rank = i + 1;
      if (sorted[i].id === viewedMemberId) return rank;
    }
    return null;
  })();
  const leagueGwAverage = league?.leagueGwAverage;

  const saveHint = (() => {
    if (saving) return t("syndicate.saving");
    const missing = data.pots.length - filledCount;
    if (missing > 0) return t("syndicate.saveHint.selectPots", { count: missing });
    if (tiebreaker === "") return t("syndicate.saveHint.enterTiebreaker");
    return t("syndicate.savePicks");
  })();

  const metaItems: React.ReactNode[] = [
    competitionName,
    <span key="mode" style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
      <ModeIconSmall mode="SYNDICATE" color={MODE_COLOR_VAR.SYNDICATE} />
      <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: MODE_COLOR_VAR.SYNDICATE }}>
        {t(`${MODE_CONTENT.SYNDICATE.keyRoot}.label`)}
      </span>
    </span>,
    competitionSeason,
  ];

  return (
    <div style={widePageContainer}>
      {/* Header card */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {metaItems.map((s, i) => (
            <React.Fragment key={i}>
              {typeof s === "string"
                ? <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{s}</span>
                : s}
              <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            </React.Fragment>
          ))}
          <StatusDot status={leagueStatus} />
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: "0 0 0.25rem", fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
              {isMemberView ? memberName : (user.displayName ?? "")}{t("syndicate.titleSuffix")}
            </h1>
            {!isMemberView && !data.isLocked && (
              <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)" }}>
                {t("syndicate.pickOnePerPot", { count: data.pots.length })}
              </p>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            <button onClick={() => setInfoOpen(true)} style={iconBtn} title={t("syndicate.howToPlayTitle")}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 7v5M8 5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <Link to={`/leagues/${leagueId}`} style={{ ...secondaryBtn, textDecoration: "none", fontSize: "0.85rem" }}>
              {t("syndicate.backToLeague")}
            </Link>
            {!data.isLocked && !isMemberView && (
              <button onClick={handleSave} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1 }}>
                {saveHint}
              </button>
            )}
          </div>
        </div>
        {saveError && <p style={{ color: "var(--color-danger)", fontSize: "0.82rem", margin: "0.5rem 0 0" }}>{saveError}</p>}

        {!data.isLocked && !isMemberView && (
          <>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
              <StatBox label={t("syndicate.stats.selections")} value={`${filledCount}/${data.pots.length}`} sub={t("syndicate.stats.potsFilled")} borderColor="var(--color-accent)" />
              <StatBox label={t("syndicate.stats.tiebreaker")} value={tiebreaker} sub={t("syndicate.stats.yourPrediction")} locked={tiebreaker === ""} borderColor="var(--color-accent)" />
            </div>
            <div style={{ height: 6, borderRadius: 3, background: "var(--color-surface-alt)", marginTop: 10, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${data.pots.length > 0 ? (filledCount / data.pots.length) * 100 : 0}%`,
                  background: "var(--color-accent)",
                  transition: "width 0.2s",
                }}
              />
            </div>
          </>
        )}
        {data.isLocked && data.picks.length > 0 && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
            <StatBox label={t("syndicate.stats.score")} value={data.totalScore} sub={t("syndicate.stats.totalPoints")} accent borderColor="var(--color-accent)" />
            <StatBox
              label="Last GW"
              value={data.myDelta}
              subNode={leagueGwAverage != null
                ? <span style={{ fontSize: "0.7rem", color: "var(--color-text-dim)" }}>League avg.: <strong style={{ fontSize: "0.82rem", color: "var(--color-accent)" }}>{leagueGwAverage.toFixed(1)}</strong></span>
                : <span>{t("syndicate.stats.thisRound")}</span>}
              success={leagueGwAverage != null && data.myDelta > leagueGwAverage}
              danger={leagueGwAverage != null && data.myDelta < leagueGwAverage}
              borderColor="var(--color-accent)"
            />
            {myRank != null && (
              <StatBox label={t("syndicate.stats.rank")} value={`#${myRank}`} sub={t("syndicate.stats.inYourLeague")} mobileHide borderColor="var(--color-accent)" />
            )}
            <StatBox
              label={t("syndicate.stats.tiebreaker")}
              value={data.tiebreaker ?? "—"}
              subNode={
                data.tiebreaker != null && league?.syndicateGoalsToDate != null ? (
                  <div style={{ fontSize: "0.7rem", color: "var(--color-text-dim)", display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span>Actual: <strong style={{ fontSize: "0.82rem", color: "var(--color-text)" }}>{league.syndicateGoalsToDate}</strong></span>
                    <span style={{ color: "var(--color-accent)" }}>
                      ({data.tiebreaker - league.syndicateGoalsToDate > 0 ? "+" : ""}{data.tiebreaker - league.syndicateGoalsToDate})
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: "0.7rem", color: "var(--color-text-dim)" }}>{t("syndicate.stats.yourPrediction")}</div>
                )
              }
              borderColor="var(--color-accent)"
            />
          </div>
        )}
      </div>

      {/* PREPARING + viewing another member's picks: hidden until the league starts */}
      {isMemberView && !data.isLocked && (
        <div style={{ ...card, marginBottom: "1.25rem" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{t("syndicate.preparingOtherNotice")}</p>
        </div>
      )}

      {/* Post-lock: trajectory chart */}
      {data.isLocked && league && (() => {
        const trajectoryHistory = isMemberView ? data.rankHistory : league.rankHistory;
        if (!trajectoryHistory || trajectoryHistory.length === 0) return null;
        return (
          <div style={{ marginTop: 10, marginBottom: 16 }}>
            <TrajectoryPanel
              rankHistory={trajectoryHistory}
              memberCount={league.members.filter((m) => m.status === "APPROVED").length}
              currentRank={myRank != null ? `#${myRank}` : "—"}
              combined
            />
          </div>
        );
      })()}

      {/* Pot cards. Read-only (locked / member-view) pots are simple summary
          cards with no expand state, so they pack multiple per row in a
          condensed grid — stretch-aligned so a club pot's one-line badge row
          doesn't look "smaller" next to a player pot's two-line card. While
          editing, a pot's expanded panel (player picker, club grid) needs
          the full row width and only one pot is ever open at a time, so the
          accordion stays one-per-row instead of packing. Single column on
          mobile either way. */}
      {!(isMemberView && !data.isLocked) && (
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : (!data.isLocked && !isMemberView) ? "1fr" : "repeat(3, 1fr)",
        gap: 10, marginBottom: 12,
      }}>
      {[...data.pots].sort((a, b) => a.order - b.order).map((pot, i) => (
        <PotCard
          key={pot.id}
          pot={pot}
          index={i + 1}
          pick={picks[pot.id] ?? EMPTY_PICK}
          isLocked={data.isLocked}
          isMemberView={isMemberView}
          points={pointsByPot.get(pot.id) ?? 0}
          form={formByPot.get(pot.id) ?? null}
          statCount={statCountByPot.get(pot.id) ?? null}
          invalid={invalidByPot.get(pot.id) ?? false}
          competitionId={league?.competition.id ?? ""}
          clubs={clubs}
          user={user}
          isMobile={isMobile}
          isOpen={openPotId === pot.id}
          onToggle={() => toggleAccordion(pot.id)}
          onSelectClub={(clubId) => selectClub(pot.id, clubId)}
          onSelectPlayer={(player) => selectPlayer(pot.id, player)}
          onClear={() => clearPick(pot.id)}
        />
      ))}
      </div>
      )}

      {/* Tiebreaker */}
      {!(isMemberView && !data.isLocked) && (!data.isLocked || (data.isLocked && !isMemberView)) && !data.isLocked && (
        <div style={{ ...card, marginBottom: 20, padding: 16 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: "0.9rem" }}>
            {t("syndicate.tiebreakerLabel")}
          </label>
          <input
            type="number"
            min={0}
            max={999}
            value={tiebreaker}
            onChange={(e) => setTiebreaker(e.target.value)}
            placeholder={t("syndicate.tiebreakerPlaceholder")}
            style={{ width: 120, padding: "8px 12px", borderRadius: 6, border: "1.5px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", fontSize: "1rem" }}
          />
        </div>
      )}

      {!isMemberView && !data.isLocked && (
        <div style={{ marginBottom: 32 }}>
          <button onClick={handleSave} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1 }}>
            {saveHint}
          </button>
        </div>
      )}

      {infoOpen && <HowToPlayModal mode="SYNDICATE" onClose={() => setInfoOpen(false)} />}
    </div>
  );
}

// ─── PotCard ────────────────────────────────────────────────────────────────
//
// Two rendering modes. Post-lock / member-view is read-only and flat — every
// pot always shows its final state, no toggle. Pre-lock editable is an
// accordion: a collapsed header row (numbered badge, label, fill summary,
// chevron) and an expanded panel that mounts the club grid or player picker
// only while open.

export interface PotCardProps {
  pot: SyndicatePot;
  index: number;
  pick: LocalPick;
  isLocked: boolean;
  isMemberView: boolean;
  points: number;
  /** CLUB pots: chronological W/D/L, capped to the last 5 results, `[]` if none yet. `null` for
   * a PLAYER pot, or when this pick's pot data isn't available (e.g. the admin backfill picker,
   * which never reaches the read-only branch that renders this). */
  form: ("W" | "D" | "L")[] | null;
  /** PLAYER pots: raw stat count over the scoring window, `0` if none yet. `null` for a CLUB pot
   * or an unavailable pick, same as `form`. */
  statCount: number | null;
  invalid: boolean;
  competitionId: string;
  clubs: string[];
  user: User;
  isMobile: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSelectClub: (clubId: string) => void;
  onSelectPlayer: (player: Player) => void;
  onClear: () => void;
}

export function PotCard({ pot, index, pick, isLocked, isMemberView, points, form, statCount, invalid, competitionId, clubs, user, isMobile, isOpen, onToggle, onSelectClub, onSelectPlayer, onClear }: PotCardProps) {
  const { t } = useTranslation();
  const readOnly = isLocked || isMemberView;

  const hint = pot.kind === "CLUB"
    ? t("syndicate.potHint.club", { win: pot.winPoints, draw: pot.drawPoints })
    : t("syndicate.potHint.player", { stat: t(STAT_LABEL_KEY[pot.statType ?? "GOALS"]), points: pot.pointsPer });

  const filled = !!(pick.clubId || pick.playerId);
  const selectedClub = pot.kind === "CLUB" && pick.clubId ? (pot.eligibleClubs ?? []).find((c) => c.id === pick.clubId) ?? null : null;

  const filledPlayerCard = pick.playerId && pick.playerName ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: readOnly ? 0 : "8px 10px", borderRadius: 8, border: readOnly ? "none" : "1.5px solid var(--color-accent)", background: readOnly ? "none" : "var(--color-slot-filled-bg)" }}>
      <ClubBadge config={getClubBadge(pick.playerClub ?? "")} size={readOnly ? 36 : 24} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: readOnly ? "0.95rem" : "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pick.playerName}</div>
        <div style={{ fontSize: readOnly ? "0.75rem" : "0.7rem", color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {pick.playerClub}{pick.playerPosition ? ` · ${pick.playerPosition}` : ""}
        </div>
      </div>
      {!readOnly && (
        <button onClick={onClear} aria-label={t("syndicate.removePick")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)", fontSize: "1rem", padding: 4, flexShrink: 0 }}>
          ✕
        </button>
      )}
    </div>
  ) : null;

  if (readOnly) {
    const statLabel = t(STAT_LABEL_KEY[pot.statType ?? "GOALS"]).toLowerCase();
    return (
      <div style={{ ...card, padding: 12, display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, flexWrap: "wrap", gap: 6 }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>
            {pot.label}
          </span>
          <span style={{ flexShrink: 0 }}>
            <span style={{ fontSize: "1.3rem", fontWeight: 700, color: "var(--color-accent)" }}>{points}</span>
            <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginLeft: 4 }}>{t("syndicate.pts")}</span>
          </span>
        </div>
        {/* Content fills whatever height the grid row stretches to, and
            centers — a one-line club badge and a two-line player card end
            up the same overall box size instead of the club box looking
            "smaller". */}
        <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
          {pot.kind === "CLUB" ? (
            selectedClub ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ClubBadge config={getClubBadge(selectedClub.name)} size={36} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.95rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedClub.name}</div>
                  {selectedClub.shortName && (
                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>{selectedClub.shortName}</div>
                  )}
                </div>
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-dim)" }}>{t("syndicate.noPickMade")}</p>
            )
          ) : (
            filledPlayerCard ?? <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-dim)" }}>{t("syndicate.noPickMade")}</p>
          )}
        </div>
        {filled && (
          <>
            <div style={{ borderTop: "1px solid var(--color-border-subtle)", margin: "10px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: "0.68rem", color: "var(--color-text-dim)" }}>{hint}</span>
              {pot.kind === "CLUB" && form && form.length > 0 && <WdlChips form={form} />}
              {pot.kind === "PLAYER" && statCount !== null && (
                <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                  {statCount} {statLabel}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  const summaryNode = invalid ? (
    <span style={{ color: "var(--color-warning)", fontWeight: 600, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
      ⚠ {t("syndicate.repickNeeded")}
    </span>
  ) : filled ? (
    <span style={{ color: "var(--color-accent)", fontWeight: 600, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? 90 : 120 }}>
      ✓ {pot.kind === "CLUB" ? (selectedClub?.shortName ?? selectedClub?.name ?? "") : pick.playerName}
    </span>
  ) : (
    <span style={{ color: "var(--color-text-dim)", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{t("syndicate.notPickedYet")}</span>
  );

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", gridColumn: isOpen && !isMobile ? "1 / -1" : undefined }}>
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}
      >
        <span style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--color-surface-alt)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.66rem", fontWeight: 700, color: "var(--color-text-muted)", flexShrink: 0 }}>
          {index}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: "0.88rem", fontWeight: 700 }}>{pot.label}</span>
          {!isMobile && <span style={{ marginLeft: 6, fontSize: "0.66rem", color: "var(--color-text-dim)" }}>{hint}</span>}
        </div>
        {summaryNode}
        <span style={{ fontSize: "0.8rem", color: "var(--color-text-dim)", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>▾</span>
      </button>

      {/* Grid-rows transition (not conditional mount + max-height) so the
          expand/collapse animates smoothly instead of snapping open/shut —
          same trick CreateLeaguePage uses for its accordion. The player
          picker still only mounts while isOpen (kept lazy: one network fetch
          per expand, filters reset on re-open), so a PLAYER pot's collapse
          animates against emptied content rather than a measured height —
          an accepted minor rough edge, opening (the far more common and
          noticeable action) is fully smooth either way. */}
      <div style={{ display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.22s ease" }}>
        <div style={{ overflow: "hidden" }}>
          <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--color-border-subtle)", paddingTop: 12 }}>
            {invalid && (
              <div style={{ background: "color-mix(in srgb, var(--color-warning) 12%, transparent)", border: "1px solid var(--color-warning)", borderRadius: 8, padding: "0.5rem 0.75rem", marginBottom: 10, fontSize: "0.8rem", color: "var(--color-warning)" }}>
                {t("syndicate.pickInvalid")}
              </div>
            )}

            {pot.kind === "CLUB" ? (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(auto-fit, minmax(90px, 1fr))" : "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                {(pot.eligibleClubs ?? []).map((club) => {
                  const selected = pick.clubId === club.id;
                  return (
                    <div
                      key={club.id}
                      onClick={() => onSelectClub(club.id)}
                      style={{
                        padding: "8px 6px", borderRadius: 8,
                        border: `1.5px solid ${selected ? "var(--color-accent)" : "var(--color-border)"}`,
                        background: selected ? "var(--color-slot-filled-bg)" : "var(--color-surface)",
                        cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                        transition: "border-color 0.15s", textAlign: "center",
                      }}
                    >
                      <ClubBadge config={getClubBadge(club.name)} size={28} />
                      <span style={{ fontWeight: 700, fontSize: "0.8rem", lineHeight: 1.2, wordBreak: "break-word" }}>
                        {club.shortName ?? club.name}
                      </span>
                    </div>
                  );
                })}
                {(pot.eligibleClubs ?? []).length === 0 && (
                  <p style={{ fontSize: "0.85rem", color: "var(--color-text-dim)", margin: 0, gridColumn: "1 / -1" }}>{t("syndicate.noClubsInPot")}</p>
                )}
              </div>
            ) : filledPlayerCard ? (
              filledPlayerCard
            ) : isOpen ? (
              <>
                {(pot.excludedPlayers ?? []).length > 0 && (
                  <p style={{ margin: "0 0 10px", fontSize: "0.78rem", color: "var(--color-warning)", fontWeight: 600 }}>
                    {t("syndicate.notSelectable", { names: (pot.excludedPlayers ?? []).map((p) => p.name).join(", ") })}
                  </p>
                )}
                <SyndicatePlayerPicker
                  user={user}
                  competitionId={competitionId}
                  clubs={clubs}
                  excludePlayerIds={(pot.excludedPlayers ?? []).map((p) => p.id)}
                  isMobile={isMobile}
                  onSelectPlayer={onSelectPlayer}
                />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
